// Friends.js - Friend management component
import { useState, useEffect, useCallback } from 'react';
import Badges from '../Badges/Badges';
import api from '../../Services/Api';
import styles from './Friends.module.css';

function Friends({ userData }) {
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({ received: [], sent: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [removeModal, setRemoveModal] = useState({ show: false, friend: null });
  const [badgesModal, setBadgesModal] = useState({ show: false, user: null, isFriend: false });
  // Open badges modal
  const openBadgesModal = (user, isFriend) => {
    console.log('Opening Badges Modal for:', { id: user.id, username: user.username }, 'Is Friend:', isFriend);
    setBadgesModal({ show: true, user: { id: user.id, username: user.username }, isFriend });
  };

  // Close badges modal
  const closeBadgesModal = () => {
    setBadgesModal({ show: false, user: null, isFriend: false });
  };

  // Fetch friends and pending requests on mount
  useEffect(() => {
    fetchFriendsData();
  }, []);

  // Debounced search - search as user types
  useEffect(() => {
    // Don't search if query is too short
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    // Debounce: wait 400ms after user stops typing
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 400);

    // Cleanup timeout if user types again
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchFriendsData = async () => {
    setLoading(true);
    try {
      const [friendsRes, pendingRes] = await Promise.all([
        api.get('friends/'),
        api.get('friends/pending/')
      ]);
      setFriends(friendsRes.data);
      setPendingRequests(pendingRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to load friends data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const performSearch = async (query) => {
    if (query.trim().length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await api.get(`users/search/?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setHasSearched(true);
      setError(null);
    } catch (err) {
      setError('Search failed');
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle form submit (for enter key)
  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) {
      setError('Search query must be at least 2 characters');
      return;
    }
    performSearch(searchQuery);
  };

  // Send friend request
  const sendFriendRequest = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post('friends/request/', { to_user_id: userId });
      // Update search results to reflect pending status
      setSearchResults(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, has_pending_request: true } 
            : user
        )
      );
      // Refresh pending requests
      const pendingRes = await api.get('friends/pending/');
      setPendingRequests(pendingRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send friend request');
    } finally {
      setActionLoading(null);
    }
  };

  // Accept friend request
  const acceptRequest = async (requestId) => {
    setActionLoading(requestId);
    try {
      await api.post(`friends/accept/${requestId}/`);
      await fetchFriendsData();
    } catch (err) {
      setError('Failed to accept request');
    } finally {
      setActionLoading(null);
    }
  };

  // Reject friend request
  const rejectRequest = async (requestId) => {
    setActionLoading(requestId);
    try {
      await api.post(`friends/reject/${requestId}/`);
      setPendingRequests(prev => ({
        ...prev,
        received: prev.received.filter(r => r.id !== requestId)
      }));
    } catch (err) {
      setError('Failed to reject request');
    } finally {
      setActionLoading(null);
    }
  };

  // Cancel sent request
  const cancelRequest = async (requestId) => {
    setActionLoading(requestId);
    try {
      await api.delete(`friends/cancel/${requestId}/`);
      setPendingRequests(prev => ({
        ...prev,
        sent: prev.sent.filter(r => r.id !== requestId)
      }));
    } catch (err) {
      setError('Failed to cancel request');
    } finally {
      setActionLoading(null);
    }
  };

  // Open remove confirmation modal
  const openRemoveModal = (friend) => {
    setRemoveModal({ show: true, friend });
  };

  // Close remove modal
  const closeRemoveModal = () => {
    setRemoveModal({ show: false, friend: null });
  };

  // Remove friend (called after confirmation)
  const confirmRemoveFriend = async () => {
    if (!removeModal.friend) return;
    
    const friendId = removeModal.friend.id;
    setActionLoading(friendId);
    closeRemoveModal();
    
    try {
      await api.delete(`friends/remove/${friendId}/`);
      setFriends(prev => prev.filter(f => f.id !== friendId));
    } catch (err) {
      setError('Failed to remove friend');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading friends...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>👥 Friends</h1>
        <p className={styles.subtitle}>Connect with others on the same journey</p>
      </div>

      {error && (
        <div className={styles.error}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'friends' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          Friends ({friends.length})
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'requests' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Requests 
          {pendingRequests.received.length > 0 && (
            <span className={styles.badge}>{pendingRequests.received.length}</span>
          )}
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'search' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('search')}
        >
          Find Friends
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        
        {/* Friends List */}
        {activeTab === 'friends' && (
          <div className={styles.friendsList}>
            {friends.length === 0 ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>👋</span>
                <h3>No friends yet</h3>
                <p>Search for users to add as friends and compete on the leaderboard!</p>
                <button 
                  className={styles.primaryButton}
                  onClick={() => setActiveTab('search')}
                >
                  Find Friends
                </button>
              </div>
            ) : (
              friends.map(friend => (
                <div key={friend.id} className={styles.friendCard}>
                  <div className={styles.friendAvatar}>
                    {friend.username.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.friendInfo}>
                    <h3>{friend.username}</h3>
                  </div>
                  <div className={styles.friendActions}>
                    <button
                      className={styles.achievementsBtn}
                      onClick={() => openBadgesModal(friend, true)}
                    >
                      🏅 Badges
                    </button>
                    <button
                      className={styles.removeBtn}
                      onClick={() => openRemoveModal(friend)}
                      disabled={actionLoading === friend.id}
                    >
                      {actionLoading === friend.id ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pending Requests */}
        {activeTab === 'requests' && (
          <div className={styles.requestsSection}>
            {/* Received Requests */}
            <div className={styles.requestGroup}>
              <h3>Received Requests</h3>
              {pendingRequests.received.length === 0 ? (
                <p className={styles.noRequests}>No pending requests</p>
              ) : (
                pendingRequests.received.map(request => (
                  <div key={request.id} className={styles.requestCard}>
                    <div className={styles.friendAvatar}>
                      {request.from_user_details.username.charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.requestInfo}>
                      <h4>{request.from_user_details.username}</h4>
                      <span className={styles.requestTime}>
                        {new Date(request.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className={styles.requestActions}>
                      <button
                        className={styles.acceptButton}
                        onClick={() => acceptRequest(request.id)}
                        disabled={actionLoading === request.id}
                      >
                        {actionLoading === request.id ? '...' : '✓ Accept'}
                      </button>
                      <button
                        className={styles.rejectButton}
                        onClick={() => rejectRequest(request.id)}
                        disabled={actionLoading === request.id}
                      >
                        {actionLoading === request.id ? '...' : '✕ Decline'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Sent Requests */}
            <div className={styles.requestGroup}>
              <h3>Sent Requests</h3>
              {pendingRequests.sent.length === 0 ? (
                <p className={styles.noRequests}>No sent requests</p>
              ) : (
                pendingRequests.sent.map(request => (
                  <div key={request.id} className={styles.requestCard}>
                    <div className={styles.friendAvatar}>
                      {request.to_user_details.username.charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.requestInfo}>
                      <h4>{request.to_user_details.username}</h4>
                      <span className={styles.requestTime}>
                        Sent {new Date(request.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className={styles.cancelButton}
                      onClick={() => cancelRequest(request.id)}
                      disabled={actionLoading === request.id}
                    >
                      {actionLoading === request.id ? '...' : 'Cancel'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search Users */}
        {activeTab === 'search' && (
          <div className={styles.searchSection}>
            <form onSubmit={handleSearch} className={styles.searchForm}>
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {isSearching && (
                <span className={styles.searchingIndicator}>🔄</span>
              )}
            </form>

            <div className={styles.searchResults}>
              {/* Show hint when query is too short */}
              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <p className={styles.searchHint}>Type at least 2 characters to search...</p>
              )}
              
              {/* Show searching indicator */}
              {isSearching && (
                <p className={styles.searchHint}>Searching...</p>
              )}
              
              {/* Show no results only after a search was performed */}
              {!isSearching && hasSearched && searchResults.length === 0 && searchQuery.length >= 2 && (
                <p className={styles.noResults}>No users found matching "{searchQuery}"</p>
              )}
              
              {searchResults.map(user => (
                <div key={user.id} className={styles.searchResultCard}>
                  <div className={styles.friendAvatar}>
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.userInfo}>
                    <h4>{user.username}</h4>
                  </div>
                  <div className={styles.userAction}>
                    <button
                      className={styles.achievementsBtn}
                      onClick={() => openBadgesModal(user, user.is_friend)}
                    >
                      🏅 Badges
                    </button>
                    {user.is_friend ? (
                      <span className={styles.friendBadge}>✓ Friends</span>
                    ) : user.has_pending_request ? (
                      <span className={styles.pendingBadge}>⏳ Pending</span>
                    ) : (
                      <button
                        className={styles.addButton}
                        onClick={() => sendFriendRequest(user.id)}
                        disabled={actionLoading === user.id}
                      >
                        {actionLoading === user.id ? '...' : '+ Add Friend'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
                  {/* Achievements Modal */}
                  {badgesModal.show && badgesModal.user && (
                    (() => { console.log('Rendering Badges Modal:', badgesModal); return null; })(),
                    <div className={styles.modalOverlay} onClick={closeBadgesModal}>
                      <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 600, width: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className={styles.modalHeader}>
                          <h2>Badges - {badgesModal.user.username}</h2>
                          <button className={styles.closeBtn} onClick={closeBadgesModal}>×</button>
                        </div>
                        <div className={styles.modalContent}>
                          {badgesModal.isFriend ? (
                            <Badges userId={badgesModal.user.id} showOnlyEarned={true} />
                          ) : (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                              <p style={{ fontSize: '1.2rem' }}>Add friend to see badges.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
            </div>
          </div>
        )}
      </div>

      {/* Badges Modal */}
      {badgesModal.show && badgesModal.user && (
        <div className={styles.modalOverlay} onClick={closeBadgesModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 600, width: '90vw', maxHeight: '80vh' }}>
            <div className={styles.modalHeader}>
              <h2>Badges - {badgesModal.user.username}</h2>
              <button className={styles.closeBtn} onClick={closeBadgesModal}>×</button>
            </div>
            <div className={styles.modalContent} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {badgesModal.isFriend ? (
                <Badges userId={badgesModal.user.id} showOnlyEarned={true} />
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ fontSize: '1.2rem' }}>Add friend to see badges.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove Friend Confirmation Modal */}
      {removeModal.show && removeModal.friend && (
        <div className={styles.modalOverlay} onClick={closeRemoveModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p className={styles.modalText}>
              Remove <strong>{removeModal.friend.username}</strong> from friends?
            </p>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={closeRemoveModal}>
                Cancel
              </button>
              <button className={styles.confirmRemoveBtn} onClick={confirmRemoveFriend}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Friends;
